/**
 * AutoBlockPublisherWhenFraudThresholdHitPage — publisher auto-block rules.
 * Shows threshold config inputs, a live prediction of the resulting decision,
 * a recent-block feed, and a blocked/warned summary. GREEN accent only.
 */
import { useMemo, useState, type FormEvent } from "react";
import {
	useAutoBlockList,
	useAutoBlockSummary,
	useAutoBlockCreate,
} from "./hooks";
import {
	DEFAULT_THRESHOLD,
	predictSeverity,
	predictStatus,
	type BlockStatus,
} from "./api";

const CARD = "rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const INPUT =
	"w-full rounded-[6px] border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 focus:border-green-600 focus:outline-none focus:ring-2 focus:ring-green-600/20";
const BTN =
	"rounded-[6px] border border-green-700 bg-green-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-green-800 disabled:cursor-not-allowed disabled:opacity-60";
const BTN_GHOST =
	"rounded-[6px] border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium text-zinc-700 hover:bg-zinc-50";
const CHECKBOX = "h-4 w-4 rounded border-zinc-300 text-green-700 focus:ring-green-600/30";

function fmtTime(unix: number): string {
	if (!Number.isFinite(unix) || unix <= 0) return "";
	return new Date(unix * 1000).toISOString().replace("T", " ").slice(0, 19);
}

function statusPill(status: BlockStatus): string {
	if (status === "blocked") return "border-green-700 bg-green-50 text-green-800";
	if (status === "warned") return "border-zinc-400 bg-zinc-100 text-zinc-800";
	return "border-zinc-200 bg-white text-zinc-500";
}

export function AutoBlockPublisherWhenFraudThresholdHitPage() {
	const listQuery = useAutoBlockList();
	const summaryQuery = useAutoBlockSummary();
	const createMut = useAutoBlockCreate();

	const [thresholdStr, setThresholdStr] = useState<string>(String(DEFAULT_THRESHOLD));
	const [windowSecStr, setWindowSecStr] = useState<string>("300");
	const [fraudCountStr, setFraudCountStr] = useState<string>("0");
	const [publisherId, setPublisherId] = useState<string>("");
	const [eventId, setEventId] = useState<string>("");
	const [autoUnblock, setAutoUnblock] = useState<boolean>(false);
	const [enabled, setEnabled] = useState<boolean>(true);

	const threshold = useMemo(() => {
		const n = Number(thresholdStr);
		return Number.isFinite(n) && n > 0 ? Math.floor(n) : DEFAULT_THRESHOLD;
	}, [thresholdStr]);

	const fraudCount = useMemo(() => {
		const n = Number(fraudCountStr);
		return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
	}, [fraudCountStr]);

	const windowSec = useMemo(() => {
		const n = Number(windowSecStr);
		return Number.isFinite(n) && n >= 0 ? Math.floor(n) : 0;
	}, [windowSecStr]);

	const predicted = useMemo(
		() => ({
			status: predictStatus(fraudCount, threshold),
			severity: predictSeverity(fraudCount, threshold),
		}),
		[fraudCount, threshold],
	);

	const canSubmit =
		enabled &&
		eventId.trim().length > 0 &&
		publisherId.trim().length > 0 &&
		threshold > 0 &&
		fraudCount >= 0 &&
		!createMut.isPending;

	const handleSubmit = (e: FormEvent) => {
		e.preventDefault();
		if (!canSubmit) return;
		createMut.mutate(
			{
				event_id: eventId.trim(),
				publisher_id: publisherId.trim(),
				fraud_count: fraudCount,
				threshold,
				window_sec: windowSec,
				reason: autoUnblock ? "auto_unblock_enabled" : "",
			},
			{
				onSuccess: () => {
					setEventId("");
				},
			},
		);
	};

	const items = listQuery.data?.items ?? [];
	const summary = summaryQuery.data;
	const totalBlocked =
		summary?.by_status.find((s) => s.status === "blocked")?.count ?? 0;
	const totalWarned =
		summary?.by_status.find((s) => s.status === "warned")?.count ?? 0;

	return (
		<main
			data-testid="auto-block-publisher-when-fraud-threshold-hit-page"
			className="mx-auto flex w-full max-w-5xl flex-col gap-8 px-6 py-10"
		>
			<header className="flex flex-col gap-2">
				<p className="text-xs font-semibold uppercase tracking-wider text-zinc-500">
					auto-block-publisher-when-fraud-threshold-hit
				</p>
				<h1 className="text-3xl font-semibold text-green-800">
					Publisher auto-block rules
				</h1>
				<p className="text-sm text-zinc-600">
					When a publisher's fraud-signal count crosses the threshold inside the
					configured window, the publisher is blocked automatically.
				</p>
			</header>

			<section className={CARD} aria-label="Rule configuration">
				<div className="mb-3 flex items-center justify-between">
					<h2 className="text-base font-semibold text-zinc-900">Rule</h2>
					<span
						className={`rounded-[6px] border px-2 py-0.5 text-xs font-medium ${
							enabled
								? "border-green-700 bg-green-50 text-green-800"
								: "border-zinc-300 bg-white text-zinc-600"
						}`}
						data-testid="auto-block-enabled-pill"
					>
						{enabled ? "enabled" : "disabled"}
					</span>
				</div>
				<form
					onSubmit={handleSubmit}
					className="grid grid-cols-1 gap-4 sm:grid-cols-2"
					data-testid="auto-block-rule-form"
				>
					<label className="flex flex-col gap-1 text-xs font-medium text-zinc-700">
						Threshold (fraud signals)
						<input
							type="number"
							min={1}
							step={1}
							className={INPUT}
							value={thresholdStr}
							onChange={(e) => setThresholdStr(e.currentTarget.value)}
							data-testid="auto-block-threshold-input"
						/>
					</label>
					<label className="flex flex-col gap-1 text-xs font-medium text-zinc-700">
						Window (seconds)
						<input
							type="number"
							min={0}
							step={1}
							className={INPUT}
							value={windowSecStr}
							onChange={(e) => setWindowSecStr(e.currentTarget.value)}
							data-testid="auto-block-window-input"
						/>
					</label>
					<label className="flex flex-col gap-1 text-xs font-medium text-zinc-700">
						Publisher id
						<input
							type="text"
							className={INPUT}
							placeholder="pub_123"
							value={publisherId}
							onChange={(e) => setPublisherId(e.currentTarget.value)}
							data-testid="auto-block-publisher-input"
						/>
					</label>
					<label className="flex flex-col gap-1 text-xs font-medium text-zinc-700">
						Event id
						<input
							type="text"
							className={INPUT}
							placeholder="evt_abc"
							value={eventId}
							onChange={(e) => setEventId(e.currentTarget.value)}
							data-testid="auto-block-event-input"
						/>
					</label>
					<label className="flex flex-col gap-1 text-xs font-medium text-zinc-700">
						Current fraud count
						<input
							type="number"
							min={0}
							step={1}
							className={INPUT}
							value={fraudCountStr}
							onChange={(e) => setFraudCountStr(e.currentTarget.value)}
							data-testid="auto-block-fraud-count-input"
						/>
					</label>
					<div className="flex flex-col gap-2 text-xs font-medium text-zinc-700">
						<label className="flex items-center gap-2">
							<input
								type="checkbox"
								checked={enabled}
								className={CHECKBOX}
								onChange={(e) => setEnabled(e.currentTarget.checked)}
								data-testid="auto-block-enabled-input"
							/>
							Rule enabled
						</label>
						<label className="flex items-center gap-2">
							<input
								type="checkbox"
								checked={autoUnblock}
								className={CHECKBOX}
								onChange={(e) => setAutoUnblock(e.currentTarget.checked)}
								data-testid="auto-block-auto-unblock-input"
							/>
							Auto-unblock when fraud drops
						</label>
					</div>

					<div
						className="col-span-1 rounded-[6px] border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-700 sm:col-span-2"
						data-testid="auto-block-prediction"
					>
						<div className="font-semibold text-zinc-800">
							Predicted decision for these inputs
						</div>
						<div className="mt-1 flex flex-wrap items-center gap-2">
							<span
								className={`rounded-[6px] border px-2 py-0.5 font-mono ${statusPill(
									predicted.status,
								)}`}
								data-testid="auto-block-predicted-status"
							>
								{predicted.status}
							</span>
							<span
								className="rounded-[6px] border border-zinc-300 bg-white px-2 py-0.5 font-mono text-zinc-700"
								data-testid="auto-block-predicted-severity"
							>
								severity: {predicted.severity}
							</span>
							<span
								className="font-mono text-zinc-600"
								data-testid="auto-block-predicted-ratio"
							>
								{fraudCount} / {threshold}
							</span>
						</div>
					</div>

					<div className="col-span-1 flex items-center gap-3 sm:col-span-2">
						<button
							type="submit"
							className={BTN}
							disabled={!canSubmit}
							data-testid="auto-block-submit"
						>
							{createMut.isPending ? "Saving." : "Apply rule and record"}
						</button>
						<button
							type="button"
							className={BTN_GHOST}
							onClick={() => {
								setThresholdStr(String(DEFAULT_THRESHOLD));
								setWindowSecStr("300");
								setFraudCountStr("0");
								setPublisherId("");
								setEventId("");
							}}
							data-testid="auto-block-reset"
						>
							Reset
						</button>
					</div>
				</form>
				{createMut.error ? (
					<p className="mt-3 text-xs text-red-700" role="alert">
						{createMut.error.message}
					</p>
				) : null}
				{createMut.data ? (
					<p
						className="mt-3 text-xs text-zinc-700"
						data-testid="auto-block-create-result"
					>
						Recorded {createMut.data.skipped ? "skip" : createMut.data.status} for{" "}
						<span className="font-mono">{createMut.data.publisher_id}</span> at
						threshold {createMut.data.threshold}.
					</p>
				) : null}
			</section>

			<section className={CARD} aria-label="Summary">
				<h2 className="text-base font-semibold text-zinc-900">Summary</h2>
				{summaryQuery.isLoading ? (
					<p
						className="mt-2 text-sm text-zinc-500"
						data-testid="auto-block-summary-loading"
					>
						Loading.
					</p>
				) : summaryQuery.error ? (
					<p className="mt-2 text-sm text-red-700" role="alert">
						{summaryQuery.error.message}
					</p>
				) : (
					<dl
						className="mt-2 grid grid-cols-3 gap-3"
						data-testid="auto-block-summary"
					>
						<div className="rounded-[6px] border border-green-700 bg-green-50 p-3">
							<dt className="text-xs font-medium text-green-800">Blocked</dt>
							<dd
								className="mt-1 text-2xl font-semibold text-green-900"
								data-testid="auto-block-total-blocked"
							>
								{totalBlocked}
							</dd>
						</div>
						<div className="rounded-[6px] border border-zinc-300 bg-white p-3">
							<dt className="text-xs font-medium text-zinc-700">Warned</dt>
							<dd
								className="mt-1 text-2xl font-semibold text-zinc-900"
								data-testid="auto-block-total-warned"
							>
								{totalWarned}
							</dd>
						</div>
						<div className="rounded-[6px] border border-zinc-200 bg-white p-3">
							<dt className="text-xs font-medium text-zinc-700">Total</dt>
							<dd
								className="mt-1 text-2xl font-semibold text-zinc-900"
								data-testid="auto-block-total-all"
							>
								{summary?.total ?? 0}
							</dd>
						</div>
					</dl>
				)}
			</section>

			<section className={CARD} aria-label="Recent blocks">
				<h2 className="text-base font-semibold text-zinc-900">Recent blocks</h2>
				{listQuery.isLoading ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="auto-block-list-loading"
					>
						Loading.
					</p>
				) : listQuery.error ? (
					<p className="mt-3 text-sm text-red-700" role="alert">
						{listQuery.error.message}
					</p>
				) : items.length === 0 ? (
					<p
						className="mt-3 text-sm text-zinc-500"
						data-testid="auto-block-list-empty"
					>
						No blocks yet.
					</p>
				) : (
					<ul
						className="mt-3 divide-y divide-zinc-200"
						data-testid="auto-block-list"
					>
						{items.map((row) => (
							<li
								key={row.id}
								className="flex flex-wrap items-center justify-between gap-2 py-2 text-sm"
								data-testid={`auto-block-row-${row.id}`}
							>
								<div className="flex flex-wrap items-center gap-2">
									<span
										className={`rounded-[6px] border px-2 py-0.5 font-mono text-xs ${statusPill(
											row.status,
										)}`}
									>
										{row.status}
									</span>
									<span className="font-mono text-xs text-zinc-700">
										{row.publisher_id}
									</span>
									<span className="font-mono text-xs text-zinc-500">
										{row.fraud_count}/{row.threshold}
									</span>
									<span className="font-mono text-xs text-zinc-500">
										sev {row.severity}
									</span>
								</div>
								<span className="font-mono text-xs text-zinc-500">
									{fmtTime(row.created_at)}
								</span>
							</li>
						))}
					</ul>
				)}
			</section>
		</main>
	);
}

export default AutoBlockPublisherWhenFraudThresholdHitPage;
